/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef } from 'react';
import { HighlightEngine, type EngineOptions } from './HighlightEngine';
import { useStore } from '@slope/store';
import { useSubscribeHighlightsSubscription } from '@slope/graphql/apollo';

type HookOptions = EngineOptions & {
  tournamentId?: string;
  pause?: boolean; // pauses subscription + loop
  wireSubscription?: boolean; // if true, attach Apollo sub and pipe into engine
};

function extractHighlightPayload(d: any) {
  return d?.highlight ?? d?.subscribeHighlights ?? d?.highlights ?? null;
}

export function useHighlightEngine(opts: HookOptions = {}) {
  const { tournamentId, pause = false, wireSubscription = true, ...engineOpts } = opts;

  // Create engine once
  const engineRef = useRef<HighlightEngine | null>(null);
  if (!engineRef.current) engineRef.current = new HighlightEngine(engineOpts);

  const engine = engineRef.current;

  // Keep engine options fresh
  useEffect(() => {
    engine.setOptions(engineOpts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    engine,
    engineOpts.maxKeep,
    engineOpts.defaultDurationMs,
    engineOpts.sweepEveryMs,
    engineOpts.minIdleGapMs,
    engineOpts.maxConsecutive,
    engineOpts.repeatIntervalMs,
  ]);

  // Mirror engine state into your store
  const storeSet = useStore(({ set }) => set);
  useEffect(() => {
    const unsub = engine.subscribe(({ current, queue }) => {
      storeSet({ highlight: current, highlights: queue });
    });
    return () => {
      unsub();
    };
  }, [engine, storeSet]);

  // Start/stop loop based on pause
  useEffect(() => {
    if (!pause) engine.start();
    else engine.stop();
    return () => engine.stop();
  }, [engine, pause]);

  console.log('useHighlightEngine', { tournamentId, pause, wireSubscription });

  const sub = useSubscribeHighlightsSubscription(
    wireSubscription
      ? {
          variables: {
            tournamentId: tournamentId!,
          },
          skip: pause,
          onError: (e) => {
            console.error('[Highlight subscription error]', e);
            setTimeout(() => sub?.restart?.(), 5000);
          },
          onData: ({ data }) => {
            const raw = extractHighlightPayload(data.data);
            if (!raw) return;
            if (tournamentId && raw.competitionId && raw.competitionId !== tournamentId) return;
            engine.enqueue(raw);
          },
        }
      : { skip: true },
  );

  return engine; // in case you want to enqueue manually, clear, etc.
}
